import { useContext, useState } from "react";
import { ButtonDS } from "get-life-storybook-ts";
import { I18nContext } from "../../../../../../context/i18n.context";
import { GetlifeContext } from "../../../../../../context/GetLifeContext.context";
import HttpLeadRepository from "../../../../../../api/request/Lead/Lead.service";
import {
  Field,
  inputStyle,
  ModificationModal,
  modalDescStyle,
  modalTitleStyle,
  SummaryRow,
  summaryBoxStyle,
} from "./shared";

interface PhoneChangeFlowProps {
  currentPhone: string;
  onClose: () => void;
  onSubmitted: () => void;
}

type Step = "form" | "otp";

const PHONE_REGEX = /^[6789]\d{8}$/;

const MSG_CODE_NOT_VALID = "El código no es correcto o ha caducado";
const MSG_TOO_MANY_ATTEMPTS = "Demasiados intentos; espera unos minutos";
const MSG_TOO_MANY_SMS =
  "Ya te hemos enviado varios códigos; espera unos minutos";
const MSG_MOBILE_ONLY = "Solo podemos enviar el código a un móvil";

const PhoneChangeFlow = ({
  currentPhone,
  onClose,
  onSubmitted,
}: PhoneChangeFlowProps) => {
  const {
    state: { translate },
  } = useContext(I18nContext);
  const { leadId, token, setNotification } = useContext(GetlifeContext);

  const [step, setStep] = useState<Step>("form");
  const [submitting, setSubmitting] = useState(false);
  const [newPhone, setNewPhone] = useState("");
  const [code, setCode] = useState("");
  const [codeError, setCodeError] = useState(false);
  const [showErrors, setShowErrors] = useState(false);

  const t = (key: string) => translate(`newDashboard.modifications.phone.${key}`);

  const normalizedPhone = newPhone.replace(/\s/g, "");
  const formValid = PHONE_REGEX.test(normalizedPhone);
  const repository = () => new HttpLeadRepository(leadId, token);

  const requestCode = async () => {
    if (!formValid) return setShowErrors(true);
    if (submitting) return;
    setSubmitting(true);
    try {
      await repository().sendPhoneChangeCode(normalizedPhone);
      setCode("");
      setCodeError(false);
      setStep("otp");
    } catch (error: any) {
      const message =
        error?.status === 429
          ? MSG_TOO_MANY_SMS
          : error?.status === 400
          ? MSG_MOBILE_ONLY
          : translate("newDashboard.modifications.errors.generic");
      setNotification({ message, type: "error", time: 5 });
    } finally {
      setSubmitting(false);
    }
  };

  const validateAndSubmit = async () => {
    if (submitting || code.trim() === "") return;
    setSubmitting(true);
    try {
      await repository().patchContactPhone(normalizedPhone, code.trim());
      onSubmitted();
      setNotification({
        message: t("successDesc"),
        type: "success",
        time: 6,
      });
      onClose();
    } catch (error: any) {
      if (error?.status === 422) {
        setCodeError(true);
        return;
      }
      const message =
        error?.status === 429
          ? MSG_TOO_MANY_ATTEMPTS
          : translate("newDashboard.modifications.errors.generic");
      setNotification({ message, type: "error", time: 5 });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModificationModal onClose={onClose}>
      {step === "form" && (
        <div>
          <h2 style={modalTitleStyle}>{t("title")}</h2>
          <p style={modalDescStyle}>{t("desc")}</p>
          <div style={summaryBoxStyle}>
            <SummaryRow label={t("current")} value={currentPhone || "—"} />
          </div>
          <Field
            label={t("newPhone")}
            error={showErrors && !formValid ? t("invalidPhone") : undefined}
          >
            <input
              style={inputStyle}
              inputMode="tel"
              maxLength={11}
              placeholder="600000000"
              value={newPhone}
              onChange={(e) => setNewPhone(e.target.value)}
            />
          </Field>
          <ButtonDS
            label={t("sendCode")}
            buttonType="primary"
            size="40"
            className="w-full"
            onClick={requestCode}
            disabled={submitting}
          />
        </div>
      )}

      {step === "otp" && (
        <div>
          <h2 style={modalTitleStyle}>{t("otpTitle")}</h2>
          <p style={modalDescStyle}>
            {t("otpDesc").replace("{phone}", normalizedPhone)}
          </p>
          <Field
            label={t("code")}
            error={codeError ? MSG_CODE_NOT_VALID : undefined}
          >
            <input
              style={{ ...inputStyle, letterSpacing: 4, textAlign: "center" }}
              inputMode="numeric"
              maxLength={6}
              value={code}
              onChange={(e) => {
                setCode(e.target.value);
                setCodeError(false);
              }}
            />
          </Field>
          <ButtonDS
            label={t("confirmChange")}
            buttonType="primary"
            size="40"
            className="w-full"
            onClick={validateAndSubmit}
            disabled={submitting || code.trim() === ""}
          />
          <button
            onClick={requestCode}
            disabled={submitting}
            style={{
              border: "none",
              background: "none",
              cursor: "pointer",
              color: "#6b7280",
              fontSize: 14,
              fontWeight: 500,
              padding: 0,
              textAlign: "center",
              width: "100%",
              marginTop: 12,
            }}
          >
            {t("resendCode")}
          </button>
        </div>
      )}

    </ModificationModal>
  );
};

export default PhoneChangeFlow;
